import React from 'react'
import { useTheme } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import getStorage from '../../../Common/Services/Storage'
import Typography from '@mui/material/Typography'
import ActionsButton from '../ActionsButton'
import Dropdown from '../Dropdown'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import IconButton from '@mui/material/IconButton'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from './TablePagination'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import Checkbox from '@mui/material/Checkbox'
import SettingsDialog from './SettingsDialog'
import Loader from '../Loader'
import ExportAction from './ExportAction'
import {
  Table,
  Toolbar,
  BulkAndChildren,
  SettingsIcon,
  Tr,
  SelectAll,
  SelectAllLink,
  Wrapper,
  ToolbarIcons,
} from './Styled'
import { checkStorageInvalidation, createColumnsProps, createColumnsPropsWithStorage, getDisplayValue } from './Utils'
import { setStateFromEvent } from '../../Utils/Events'
import { FlexRow } from '../Flex'
import { Cached } from '@mui/icons-material'
import * as R from 'ramda'
import config from '../../../Core/Config'
import { selectCurrentUser } from '../../../Auth/Redux/Auth'
import { selectUtcEnabled } from '../../../Core/Redux/Theme'
import { Tooltip } from '@mui/material'
import { useResizableColumns } from './Hooks'
import Config from '../../../Core/Config'
import { LOCAL_REPORT_ITEM_TYPE_TABLE } from '../../../Reports/Constants'
import AddToLocalReport from '../../../Reports/Components/AddToLocalReport'
import { useSelector } from 'react-redux'

const storageService = getStorage()
const sessionService = getStorage(true)

const ChangeListTable = ({
  id,
  name,
  autoWidth,
  refreshData,
  data,
  dataCount,
  loading,
  disableLoading,
  qsAdditions,
  idField,
  selectable,
  selected,
  onSelect,
  columns,
  dftListPerPage,
  dftSortDirection,
  dftSortField,
  listDisplay,
  fieldsMapping,
  qs,
  actions,
  bulkActions,
  onAction,
  noSettings,
  noExport,
  noBulkSelection,
  onExpandRow,
  sortMapping,
  disableSorting,
  disableCheckbox,
  children,
  filterForm,
  filterFormActive,
  exportApi,
  refetch,
  disableSticky,
  disableResizing,
  noOverflow,
  absoluteToolbarIcons,
  disableSelectOnRowClick,
  dftInitPage,
  emptyContent,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  if (!id) id = name

  const storageKey = `${id}_CHANGELIST_TABLE_SETTINGS`

  // columns resizing
  const resizingData = storageService.get(`${id}_CHANGELIST_TABLE_RESIZING`, {})

  // wrapper ref
  const wrapperRef = React.useRef(null)

  // sticky
  const sticky = {
    position: 'sticky',
    background: theme.palette.content.main,
    zIndex: 2,
  }

  const stickyLeft = {
    ...sticky,
    left: 0,
  }

  const stickyRight = {
    ...sticky,
    right: 0,
  }

  // check storage for invalidation
  checkStorageInvalidation(storageKey, id)

  // logged user
  const user = useSelector(selectCurrentUser)
  // re-render formatted dates when utc setting is toggled
  useSelector(selectUtcEnabled)
  // primary key
  const primaryKeyValue = (item) =>
    idField
      .split(',')
      .map((f) => item[f])
      .join('-')

  // list per page
  const [listPerPage, setListPerPage] = React.useState(
    parseInt(storageService.getNested(storageKey, 'listPerPage', dftListPerPage)),
  )

  // sort field
  const [defaultSortField, setDefaultSortField] = React.useState(
    storageService.getNested(storageKey, 'defaultSortField', dftSortField),
  )

  const [defaultSortDirection, setDefaultSortDirection] = React.useState(
    storageService.getNested(storageKey, 'defaultSortDirection', dftSortDirection),
  )

  // pagination
  const [page, setPage] = React.useState(getInitPageState(id, dftInitPage))

  const handleSetPage = React.useCallback(
    (newPage, isManualAction) => {
      setPage(newPage)
      if (config.enableSavePagination) {
        sessionService.save(`${id}_CHANGELIST_TABLE_PAGE`, newPage)
      }
      if (Config.ui.enableChangelistTableGoToTop && wrapperRef.current && isManualAction) {
        const yOffset = -70
        const y = wrapperRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y })
      }
    },
    [setPage, Config.enableSavePagination, wrapperRef.current],
  )

  // sorting
  const sortQs = (field) => (sortMapping[field] ? sortMapping[field] : field.replace(/\./g, '/'))

  const [sort, setSort] = React.useState({
    field: sortQs(qs.base.orderby),
    direction: qs.base.ordertype,
  })
  const handleSortChange =
    (field, direction = null) =>
    () =>
      setSort({
        field: sortQs(field),
        direction:
          direction !== null ? direction : sort.field === sortQs(field) && sort.direction === 'asc' ? 'desc' : 'asc',
      })

  const handleChangeDefaultSortField = (field) => {
    setDefaultSortField(field)
    handleSortChange(field, defaultSortDirection)()
  }

  const handleChangeDefaultSortDirection = (direction) => {
    setDefaultSortDirection(direction)
    handleSortChange(defaultSortField, direction)()
  }

  // reset page when changing filtering
  const prevQsAdditions = React.useRef(JSON.stringify(qsAdditions))
  React.useEffect(() => {
    if (prevQsAdditions.current && prevQsAdditions.current !== JSON.stringify(qsAdditions)) {
      handleSetPage(0, false)
    }
    prevQsAdditions.current = JSON.stringify(qsAdditions)
  }, [JSON.stringify(qsAdditions)])

  // data refreshing
  React.useEffect(() => {
    refreshData({
      base: {
        top: Math.max(1, listPerPage),
        skip: page * listPerPage,
        orderby: sort.field,
        ordertype: sort.direction,
      },
      qsAdditions: { ...qsAdditions },
    })

    if (onExpandRow) {
      setOpenRow(null)
    }
  }, [JSON.stringify(qsAdditions), page, sort, listPerPage, refreshData])

  // bulk
  const [selectedBulk, setSelectedBulk] = React.useState(null)
  const executeBulkAction = () => {
    const action = R.find(R.propEq('id', selectedBulk))(bulkActions)
    return action && onAction(action.id)
  }
  // collapsable
  const [openRow, setOpenRow] = React.useState(null)

  // columns
  const [columnsProps, setColumnProps] = React.useState([])
  React.useEffect(() => {
    setColumnProps(createColumnsPropsWithStorage(columns, listDisplay, storageService.getNested(storageKey, 'columns')))
  }, [storageKey, listDisplay])

  // columns selection
  const handleSelectColumn = (id) => (event) => {
    setColumnProps(
      columnsProps.map((column) =>
        R.ifElse(
          R.equals(id),
          R.always({ ...column, visible: R.prop('checked')(event.target) }),
          R.always(column),
        )(column.id),
      ),
    )
  }

  // filter form
  const [filterFormIsOpen, setFilterFormIsOpen] = React.useState(false)
  const handleOpenFilterForm = () => setFilterFormIsOpen(true)
  const handleCloseFilterForm = () => setFilterFormIsOpen(false)

  // settings
  const saveSettings = () => {
    storageService.save(storageKey, {
      timestamp: new Date().getTime(),
      columns: columnsProps,
      listPerPage,
      defaultSortField: sortQs(defaultSortField),
      defaultSortDirection,
    })
    setSettingsDialogIsOpen(false)
  }

  const resetSettings = () => {
    storageService.remove(storageKey)
    setColumnProps(createColumnsProps(columns, listDisplay))
    setListPerPage(dftListPerPage)
    setDefaultSortField(dftSortField)
    setDefaultSortDirection(dftSortDirection)
    setSettingsDialogIsOpen(false)
    handleSortChange(dftSortField, dftSortDirection)()
  }

  // columns to be displayed
  const displayColumns = columnsProps.filter((c) => c.visible)

  // settings
  const [settingsDialogIsOpen, setSettingsDialogIsOpen] = React.useState(false)
  const handleOpenSettings = React.useCallback(() => setSettingsDialogIsOpen(true), [setSettingsDialogIsOpen])

  // selection
  const selectedIds = selected?.map((item) => primaryKeyValue(item))
  // page selection
  const handleSelectPage = (event) => {
    const displayDataIds = displayData.map(primaryKeyValue)
    const selectedIds = selected.map(primaryKeyValue)

    onSelect(
      R.ifElse(
        R.prop('checked'),
        R.always([
          ...selected,
          ...displayData.filter(R.pipe(primaryKeyValue, R.flip(R.indexOf)(selectedIds), R.equals(-1))),
        ]),
        R.always([...selected.filter(R.pipe(primaryKeyValue, R.flip(R.indexOf)(displayDataIds), R.equals(-1)))]),
      )(event.target),
    )
  }
  // record selection
  const handleSelectRecord = (record) => (event) =>
    onSelect(
      R.ifElse(
        R.prop('checked'),
        R.always([...selected, record]),
        R.always(selected.filter(R.compose(R.not, R.equals(primaryKeyValue(record)), primaryKeyValue))),
      )(event.target),
      record,
      event.target.checked,
    )

  // clear selection
  const handleClearSelection = () => onSelect && onSelect([])

  // display
  const displayData = data

  // col span calculation
  const colSpan = getColSpan(displayColumns, selectable, actions, onExpandRow)
  const totalItems = getTotalItems(dataCount, page, listPerPage, displayData)

  useResizableColumns(id, name, data, displayColumns, loading, {
    disabled: disableResizing,
  })

  const LocalReportManager = {
    serializeItem: () => {
      return {
        name,
        type: LOCAL_REPORT_ITEM_TYPE_TABLE,
        data: {
          columns,
          data,
          listDisplay,
          sortField: defaultSortField,
          sortDirection: defaultSortDirection,
        },
      }
    },
  }

  const FilterIcon = getFilterIcon(filterFormActive)
  const enabledBulkActions = bulkActions.filter((a) => !a.perm || a.perm(user))

  return (
    <>
      {(!!bulkActions.length || children) && (
        <BulkAndChildren>
          {!!enabledBulkActions.length && (
            <>
              <Dropdown
                label={t('common:generic.Commands')}
                width="300px"
                value={selectedBulk}
                onChange={setStateFromEvent(setSelectedBulk)}
                options={enabledBulkActions.map((a) => ({
                  value: a.id,
                  label: a.label,
                  icon: a.icon,
                }))}
              />
              <Button size="small" disabled={!selectedBulk || !selected.length} onClick={executeBulkAction}>
                {t('common:generic.Go')}
              </Button>
            </>
          )}
          {children}
        </BulkAndChildren>
      )}
      <Wrapper $flex={noOverflow} ref={wrapperRef}>
        <TablePagination
          rowsPerPageOptions={[listPerPage]}
          component="div"
          count={totalItems}
          rowsPerPage={listPerPage}
          page={page}
          onPageChange={(evt, page) => {
            handleSetPage(page, evt !== null)
          }}
          showFirstButton
          showLastButton
          hide={(loading && !displayData.length) || !Config.ui.enableChangelistPaginationOnTop}
        />
        {showToolbar(selectable, filterForm, noSettings, noExport, refetch) && (
          <Toolbar bg={theme.palette.changelistToolbar.main} color={theme.palette.changelistToolbar.contrastText}>
            <FlexRow alignCenter>
              {selectable && (
                <Typography component="div">{t('common:ui:numSelected', { num: selected.length })}</Typography>
              )}
              {selectable && !!selected.length && (
                <SelectAll component="div">
                  <SelectAllLink onClick={handleClearSelection} size="small">
                    {t('common:ui:clearSelection')}
                  </SelectAllLink>
                </SelectAll>
              )}
            </FlexRow>
            <ToolbarIcons $absolute={absoluteToolbarIcons}>
              {loading && <Loader display="inline-flex" size={18} />}
              <AddToLocalReport manager={LocalReportManager} />
              {!!refetch && (
                <Tooltip title={t('common:buttons.Refresh')}>
                  <Cached onClick={refetch} style={{ cursor: 'pointer' }} />
                </Tooltip>
              )}
              {filterForm && (
                <Tooltip title={t('common:buttons.Filter')}>
                  <FilterIcon
                    onClick={handleOpenFilterForm}
                    color={getFilterColor(filterFormActive)}
                    style={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              )}
              {!noExport && (
                <ExportAction
                  name={name}
                  displayColumns={displayColumns}
                  sortedData={displayData}
                  fieldsMapping={fieldsMapping}
                  exportApi={exportApi}
                  qs={qs}
                />
              )}
              {!noSettings && (
                <Tooltip title={t('common:ui.Settings')}>
                  <SettingsIcon onClick={handleOpenSettings} style={{ cursor: 'pointer' }} />
                </Tooltip>
              )}
            </ToolbarIcons>
          </Toolbar>
        )}
        <TableContainer sx={getTableContainerStyle(noOverflow)}>
          <Table
            style={getTableStyle(autoWidth)}
            size="small"
            id={`id-table-${name}`}
            className={getTableClassName(disableResizing)}
          >
            <TableHead key="thead">
              <TableRow key="thead-row">
                {selectable && (
                  <TableCell padding="checkbox" style={{ width: '20px' }} key="col-selection" className="fix">
                    {!noBulkSelection && selectable === true && (
                      <Checkbox
                        indeterminate={selected.length > 0 && selected.length < displayData.count}
                        checked={
                          displayData.length !== 0 &&
                          displayData.filter((item) => selectedIds.indexOf(primaryKeyValue(item)) === -1).length === 0
                        }
                        onChange={handleSelectPage}
                      />
                    )}
                  </TableCell>
                )}
                {displayColumns.map((column, idx) => {
                  const widthStyles = {
                    width: column.width ? column.width : 'auto',
                  }
                  const resizableIdx = selectable ? idx + 1 : idx
                  if (
                    config.ui.enableChangelistTableColumnsResizing &&
                    !disableResizing &&
                    resizingData[resizableIdx]
                  ) {
                    widthStyles.width = `${resizingData[resizableIdx]}px`
                    widthStyles.minWidth = `${resizingData[resizableIdx]}px`
                    widthStyles.maxWidth = `${resizingData[resizableIdx]}px`
                  }
                  return (
                    <TableCell
                      key={`col-${column.id}`}
                      align={column.numeric ? 'right' : 'left'}
                      sortDirection={sort.field === column.id ? sort.direction : false}
                      style={{
                        ...widthStyles,
                        ...(idx === 0 && !disableSticky ? stickyLeft : {}),
                        ...(config.ui.enableChangelistTableColumnsResizing ? { paddingRight: '24px' } : {}),
                      }}
                      className={
                        idx === displayColumns.length - 1 && !actions?.length ? 'fix th-col-name' : 'th-col-name'
                      }
                    >
                      {disableSorting.indexOf(column.id) !== -1 ? (
                        column.label
                      ) : (
                        <TableSortLabel
                          active={sort.field === sortQs(column.id)}
                          direction={sort.field === sortQs(column.id) ? sort.direction : 'asc'}
                          onClick={handleSortChange(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  )
                })}
                {showActionsCell(actions, onExpandRow) && (
                  <TableCell key="col-actions" style={disableSticky ? {} : stickyRight} className="fix" />
                )}
              </TableRow>
            </TableHead>
            {!loading && !displayData.length && emptyContent && (
              <TableBody key="tbody-empty">
                <TableRow key="tbody-row">
                  <TableCell colSpan={getEmptyContentColSpan(displayColumns, selectable, actions)} align="center">
                    {emptyContent}
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
            {(!loading || !!displayData.length) && (
              <TableBody key="tbody">
                {displayData.map((record, index) => {
                  const allowedActions =
                    actions && actions.length
                      ? actions.filter((a) => {
                          return (!a.perm || a.perm(user, record)) && (!a.cond || a.cond(record))
                        })
                      : []
                  return (
                    <React.Fragment key={`row-${index}`}>
                      <Tr
                        data-no-border={!!onExpandRow}
                        hover
                        onClick={() => {
                          return (selectable === true || (selectable && selectable(record))) &&
                            Config.ui.enableChangelistTableClickOnRowSelect &&
                            !disableSelectOnRowClick
                            ? handleSelectRecord(record)({
                                target: {
                                  checked:
                                    selected.filter(R.compose(R.equals(primaryKeyValue(record)), primaryKeyValue))
                                      .length !== 1,
                                },
                              })
                            : null
                        }}
                      >
                        {selectable && (
                          <TableCell padding="checkbox">
                            {(selectable === true || (selectable && selectable(record))) && (
                              <Checkbox
                                checked={
                                  selected.filter(R.compose(R.equals(primaryKeyValue(record)), primaryKeyValue))
                                    .length === 1
                                }
                                onChange={handleSelectRecord(record)}
                                disabled={disableCheckbox(record)}
                              />
                            )}
                          </TableCell>
                        )}
                        {displayColumns.map((column, idx) => {
                          const widthStyles = {
                            width: column.width ? column.width : 'auto',
                          }
                          const resizableIdx = selectable ? idx + 1 : idx
                          if (
                            config.ui.enableChangelistTableColumnsResizing &&
                            !disableResizing &&
                            resizingData[resizableIdx]
                          ) {
                            widthStyles.width = `${resizingData[resizableIdx]}px`
                            widthStyles.minWidth = `${resizingData[resizableIdx]}px`
                            widthStyles.maxWidth = `${resizingData[resizableIdx]}px`
                          }
                          return (
                            <TableCell
                              key={`col-${column.id}`}
                              title={column.label}
                              align={column.numeric ? 'right' : 'left'}
                              style={{
                                ...R.defaultTo({}, column.style),
                                ...widthStyles,
                                ...(idx === 0 && !disableSticky ? stickyLeft : {}),
                              }}
                            >
                              {getDisplayValue(fieldsMapping, record, column.id, column)}
                            </TableCell>
                          )
                        })}
                        {((actions && actions.length) || onExpandRow) && (
                          <TableCell
                            align="right"
                            key={`col-actions-${index}`}
                            style={disableSticky ? {} : stickyRight}
                          >
                            {onExpandRow && (
                              <IconButton
                                size="small"
                                onClick={(evt) => {
                                  evt.stopPropagation()
                                  setOpenRow(openRow === index ? null : index)
                                }}
                              >
                                {openRow === index ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            )}

                            {!!allowedActions.length && (
                              <ActionsButton
                                actions={allowedActions}
                                onClick={(action, evt) => {
                                  evt.stopPropagation()
                                  onAction(action.id, record, evt)
                                }}
                              />
                            )}
                          </TableCell>
                        )}
                      </Tr>
                      {onExpandRow && (
                        <TableRow key={`expand-${index}`}>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={colSpan}>
                            <Collapse in={openRow === index} timeout="auto" unmountOnExit>
                              {onExpandRow(record)}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  )
                })}
              </TableBody>
            )}
          </Table>
          <Loader minHeight="100px" skeleton={listPerPage} show={!displayData.length && loading && !disableLoading} />
        </TableContainer>
      </Wrapper>
      <TablePagination
        rowsPerPageOptions={[listPerPage]}
        component="div"
        count={totalItems}
        rowsPerPage={listPerPage}
        page={page}
        onPageChange={(evt, page) => handleSetPage(page, evt !== null)}
        showFirstButton
        showLastButton
        hide={loading && !displayData.length}
      />
      {settingsDialogIsOpen && (
        <SettingsDialog
          columns={columnsProps}
          open={settingsDialogIsOpen}
          handleClose={() => setSettingsDialogIsOpen(false)}
          handleReset={resetSettings}
          handleSaveAndClose={saveSettings}
          onSelect={handleSelectColumn}
          onSort={setColumnProps}
          listPerPage={listPerPage}
          onListPerPageChange={(value) => setListPerPage(value ? parseInt(value) : '')}
          disableSorting={disableSorting}
          sortMapping={sortMapping}
          defaultSortField={defaultSortField}
          defaultSortDirection={defaultSortDirection}
          onDefaultSortFieldChange={handleChangeDefaultSortField}
          onDefaultSortDirectionChange={handleChangeDefaultSortDirection}
        />
      )}
      {filterFormIsOpen && filterForm(handleCloseFilterForm)}
    </>
  )
}

ChangeListTable.defaultProps = {
  idField: 'id',
  qsAdditions: {},
  autoWidth: false,
  sortDirection: 'asc',
  fieldsMapping: {},
  bulkActions: [],
  dftInitPage: 0,
  dftListPerPage: 10,
  dftSortField: 'id',
  dftSortDirection: 'asc',
  disableSorting: [],
  disableCheckbox: R.always(false),
  sortMapping: {},
}

ChangeListTable.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  refreshData: PropTypes.func,
  autoWidth: PropTypes.bool,
  idField: PropTypes.string,
  data: PropTypes.array,
  dataCount: PropTypes.number,
  loading: PropTypes.bool,
  disableLoading: PropTypes.bool,
  qsAdditions: PropTypes.object,
  selected: PropTypes.array,
  onSelect: PropTypes.func,
  selectable: PropTypes.bool,
  columns: PropTypes.array.isRequired,
  listDisplay: PropTypes.array.isRequired,
  dftInitPage: PropTypes.number,
  dftListPerPage: PropTypes.number,
  dftSortField: PropTypes.string,
  dftSortDirection: PropTypes.string,
  qs: PropTypes.object,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
    }),
  ),
  bulkActions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
    }),
  ),
  onAction: PropTypes.func,
  noExport: PropTypes.bool,
  noSettings: PropTypes.bool,
  noBulkSelection: PropTypes.bool,
  fieldsMapping: PropTypes.object,
  onExpandRow: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  disableSorting: PropTypes.arrayOf(PropTypes.string),
  disableCheckbox: PropTypes.func,
  filterForm: PropTypes.func,
  filterFormActive: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  exportApi: PropTypes.func,
  sortMapping: PropTypes.object,
  refetch: PropTypes.func,
  disableSticky: PropTypes.bool,
  disableResizing: PropTypes.bool,
  noOverflow: PropTypes.bool,
  absoluteToolbarIcons: PropTypes.bool,
  disableSelectOnRowClick: PropTypes.bool,
  emptyContent: PropTypes.any,
}

export default React.memo(ChangeListTable)

export const getDefaultListPerPage = (name, defaultListPerPage = 10) =>
  parseInt(storageService.getNested(`${name}_CHANGELIST_TABLE_SETTINGS`, 'listPerPage', defaultListPerPage))

export const getDefaultPageNumber = (name, dft = 0) =>
  parseInt(sessionService.get(`${name}_CHANGELIST_TABLE_PAGE`, dft))

export const getDefaultSortField = (name, defaultSortField = 'id') =>
  storageService.getNested(`${name}_CHANGELIST_TABLE_SETTINGS`, 'defaultSortField', defaultSortField)

export const getDefaultSortDirection = (name, defaultSortDirection = 'asc') =>
  storageService.getNested(`${name}_CHANGELIST_TABLE_SETTINGS`, 'defaultSortDirection', defaultSortDirection)

const getInitPageState = (id, dftInitPage) => {
  return Config.enableSavePagination ? sessionService.get(`${id}_CHANGELIST_TABLE_PAGE`, 0) : dftInitPage
}

const getColSpan = (displayColumns, selectable, actions, onExpandRow) => {
  return displayColumns.length + (selectable ? 1 : 0) + (actions?.length || onExpandRow ? 1 : 0)
}

const getTotalItems = (dataCount, page, listPerPage, displayData) => {
  let totalItems = dataCount
  // end of table my friend
  if (dataCount === -1 && displayData.length < listPerPage) {
    totalItems = page * listPerPage + displayData.length
  }

  return totalItems
}

const getFilterIcon = (filterFormActive) => {
  return filterFormActive ? FilterAltIcon : FilterAltOffIcon
}

const getFilterColor = (filterFormActive) => {
  return filterFormActive ? 'primary' : undefined
}

const getTableContainerStyle = (noOverflow) => {
  return noOverflow ? { width: 'auto', overflowX: 'visible' } : { maxWidth: '100%', overflow: 'auto' }
}

const showActionsCell = (actions, onExpandRow) => {
  return (actions && actions.length) || onExpandRow
}

const showToolbar = (selectable, filterForm, noSettings, noExport, refetch) => {
  return selectable || filterForm || !noSettings || !noExport || refetch
}

const getTableStyle = (autoWidth) => {
  return autoWidth ? { width: 'auto' } : {}
}

const getTableClassName = (disableResizing) => {
  return `changelisttable${
    config.ui.enableChangelistTableColumnsResizing && !disableResizing ? ' resizable-active' : ''
  }`
}

const getEmptyContentColSpan = (displayColumns, selectable, actions) => {
  return displayColumns.length + (actions ? 1 : 0) + (selectable ? 1 : 0)
}
